import { useState } from 'react';
import { addresses, type OfficeAddress } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, MapPin, Plus, Trash2, Edit3 } from 'lucide-react';

const emptyAddress: OfficeAddress = {
  id: '',
  label: '',
  name: '',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postcode: '',
  country: '',
  phone: '',
  email: '',
  hours: '',
  isActive: true,
};

export default function Addresses() {
  const [items, setItems] = useState<OfficeAddress[]>(addresses.get());
  const [editing, setEditing] = useState<OfficeAddress | null>(null);
  const [isNew, setIsNew] = useState(false);

  const handleSave = () => {
    if (!editing) return;
    let next: OfficeAddress[];
    if (isNew) {
      const newItem = { ...editing, id: `addr-${Date.now()}` };
      next = [...items, newItem];
    } else {
      next = items.map(i => i.id === editing.id ? editing : i);
    }
    setItems(next);
    addresses.set(next);
    setEditing(null);
    setIsNew(false);
    toast.success(isNew ? 'Address added' : 'Address updated');
  };

  const handleDelete = (id: string) => {
    const next = items.filter(i => i.id !== id);
    setItems(next);
    addresses.set(next);
    toast.success('Address removed');
  };

  const startNew = () => {
    setEditing({ ...emptyAddress });
    setIsNew(true);
  };

  const updateField = (field: keyof OfficeAddress, value: string | boolean) => {
    if (!editing) return;
    setEditing({ ...editing, [field]: value });
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Office Addresses</h1>
          <p className="text-white/60 text-sm mt-1">Manage UK HQ, Ireland, and EMEA office locations.</p>
        </div>
        <Button onClick={startNew} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Address
        </Button>
      </div>

      {/* Address List */}
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="card-dark p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${
                  item.isActive ? 'bg-[#FFDD00]/20 text-[#0057B7]' : 'bg-white/5 text-white/70'
                }`}>
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-white">{item.label}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.isActive ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-white/5 text-white/60'
                    }`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-white/70 mt-1">{item.name}</p>
                  <p className="text-xs text-white/70 mt-0.5">{item.line1}{item.line2 ? `, ${item.line2}` : ''}, {item.city}, {item.postcode}</p>
                  <p className="text-xs text-white/70">{item.country} | {item.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setEditing(item); setIsNew(false); }}
                  className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="card-dark p-6 space-y-4">
          <h3 className="font-heading font-semibold text-white">
            {isNew ? 'Add New Address' : 'Edit Address'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { field: 'label' as keyof OfficeAddress, label: 'Label', placeholder: 'e.g. UK HQ' },
              { field: 'name' as keyof OfficeAddress, label: 'Office Name', placeholder: 'Full office name' },
              { field: 'line1' as keyof OfficeAddress, label: 'Address Line 1', placeholder: 'Street address' },
              { field: 'line2' as keyof OfficeAddress, label: 'Address Line 2', placeholder: 'Suite, unit, etc.' },
              { field: 'city' as keyof OfficeAddress, label: 'City', placeholder: 'City' },
              { field: 'region' as keyof OfficeAddress, label: 'Region', placeholder: 'County/Province' },
              { field: 'postcode' as keyof OfficeAddress, label: 'Postcode', placeholder: 'Postcode' },
              { field: 'country' as keyof OfficeAddress, label: 'Country', placeholder: 'Country' },
              { field: 'phone' as keyof OfficeAddress, label: 'Phone', placeholder: '+44 (0)...' },
              { field: 'email' as keyof OfficeAddress, label: 'Email', placeholder: 'office@wellselectronics.co.uk' },
              { field: 'hours' as keyof OfficeAddress, label: 'Opening Hours', placeholder: 'Mon-Fri 9:00-17:00' },
            ].map(({ field, label, placeholder }) => (
              <div key={field} className="space-y-1.5">
                <Label className="text-white/80 text-xs">{label}</Label>
                <Input
                  value={editing[field] as string}
                  onChange={(e) => updateField(field, e.target.value)}
                  placeholder={placeholder}
                  className="h-10 bg-[#0057B7] border-white/10 text-white text-sm"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="isActive"
              checked={editing.isActive}
              onChange={(e) => updateField('isActive', e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-[#0057B7] text-[#FFDD00]"
            />
            <Label htmlFor="isActive" className="text-white/70 text-sm cursor-pointer">Active</Label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} className="btn-primary">
              <Save className="w-4 h-4" />
              {isNew ? 'Add Address' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setEditing(null); setIsNew(false); }}
              className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}